import i18n from 'src/lib/i18n/i18n';
import View from '@play-co/timestep-core/lib/ui/View';
import { Feature } from 'src/replicant/analytics';
import sounds from 'src/lib/sounds';
import StateObserver, { now, user } from 'src/StateObserver';
import PopupBasicLazy from '../../components/popups/PopupBasicLazy';
import { AssetGroup } from '@play-co/timestep-core';
import { ImageView } from '@play-co/timestep-core/ui';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import { createPopupDeeplink } from 'src/game/launch/deepLinks';
import { startPurchaseFlow } from 'src/lib/iap/iap.util';
import { StarterPackProductId } from 'src/replicant/match2/shopbundles/match2-shopbundles.ruleset';
import { Match2Timer } from 'src/game/minigame/match2-timestep/components/Match2Timer';
import { duration } from 'src/replicant/utils/duration';
import { getRemainingStarterPackPromoTime } from 'src/replicant/starterpack/starterpack.getters';

/**
 * The pet starter pack promo popup
 */
export default class PopupPetStarterPack extends PopupBasicLazy {
  private productId?: StarterPackProductId;
  private timer: Match2Timer;
  /**
   * Constructor
   */
  constructor(opts: { superview: View; close: () => void }) {
    super({
      width: 712,
      height: 1069,
      zIndex: 10000,
      ...opts,
      skipTitle: true,
      tracking: {
        feature: Feature.PET_STARTER_PACK._,
        $subFeature: Feature.PET_STARTER_PACK.POPUP,
      },
    });

    this.buttonClose.updateOpts({
      x: 669,
      y: 110,
    });

    this.message.updateOpts({
      width: 464,
      height: 71,
      x: (this.box.style.width - 464) * 0.5,
      y: 840,
      color: 'white',
    });

    this.timer = new Match2Timer({
      superview: this.box,
      x: 260,
      y: 160,
      roundUp: true,
    });
    this.timer.setRemainingTimeGetter(() => {
      return getRemainingStarterPackPromoTime(user(), now());
    });
  }

  private getAssets(productId: StarterPackProductId) {
    const folderMap: Record<StarterPackProductId, string> = {
      buruburu_pet_starter_pack: 'assets/promo/pet_starter_pack',
      buruburu_pet_starter_pack_2: 'assets/promo/pet_starter_pack_2',
      buruburu_pet_starter_pack_3: 'assets/promo/pet_starter_pack_3',
      buruburu_starter_pack: 'assets/promo/starter_pack',
    };

    const folder = folderMap[productId];
    if (!folder) {
      throw new Error(`Invalid productId: ${productId}`);
    }

    const locale = StateObserver.getLocale();

    const title =
      locale === 'en'
        ? `${folder}/one-time-offer-en.png`
        : `${folder}/one-time-offer-jp.png`;

    // Pack names assets are named as 'title'
    const packName =
      locale === 'en' ? `${folder}/title_en.png` : `${folder}/title_jp.png`;

    return {
      title,
      packName,
      button: `${folder}/button.png`,
      buttonPressed: `${folder}/button_pressed.png`,
      popup: `${folder}/popup_onetimeoffer.png`,
    };
  }

  /**
   * Get assets group
   */
  public getAssetsGroup() {
    if (!this.productId) {
      throw new Error('productId not set');
    }
    const assets = this.getAssets(this.productId);
    return new AssetGroup(Object.values(assets));
  }

  /**
   * On popup show
   */
  public init(args: { productId: StarterPackProductId }) {
    // getAssetsGroup will use this to load the correct assets
    this.productId = args.productId;

    super.init({
      title: i18n(`petStarterPack.title`),
      message: '',
    });

    const assets = this.getAssets(args.productId);
    const locale = StateObserver.getLocale();

    this.box.updateOpts({
      image: assets.popup,
    });

    // Popup title
    const titleConfig =
      locale === 'en'
        ? {
            x: (this.box.style.width - 389) * 0.5,
            width: 389,
            height: 86,
          }
        : {
            x: (this.box.style.width - 447) * 0.5,
            width: 447,
            height: 98,
          };
    new ImageView({
      superview: this.box,
      image: assets.title, // Already localised
      y: 37,
      ...titleConfig,
    });

    // Pack name
    const packNameConfig =
      locale === 'en'
        ? {
            x: (this.box.style.width - 337) * 0.5,
            width: 337,
            height: 47,
          }
        : {
            x: (this.box.style.width - 348) * 0.5,
            width: 348,
            height: 54,
          };
    new ImageView({
      superview: this.box,
      image: assets.packName, // Already localised
      y: 560,
      ...packNameConfig,
    });

    // Purchase button
    new ButtonScaleView({
      superview: this.box,
      image: assets.button,
      imagePressed: assets.buttonPressed,
      imageDisabled: assets.buttonPressed,
      x: (this.box.style.width - 346) * 0.5,
      y: 890,
      width: 346,
      height: 87,
      tracking: {
        feature: Feature.PET_STARTER_PACK._,
        $subFeature: Feature.PET_STARTER_PACK.POPUP_OK,
      },
      onClick: async () => {
        sounds.sfx.play('ok');
        await this.buyProduct(args.productId);
      },
    });

    this.timer.setOnUpdate((remainingTime) => {
      // Give 5 minutes grace period to close the popup after the promo ends
      if (remainingTime <= -duration({ minutes: 5 })) {
        void this.opts.close();
      }
    });
  }

  /**
   * Buy pet starter pack
   *
   * @param productId
   * @returns
   */
  private async buyProduct(productId: StarterPackProductId) {
    await startPurchaseFlow({
      productId: productId,
      feature: Feature.PET_STARTER_PACK._,
      subFeature: Feature.PET_STARTER_PACK.PURCHASE,
      onComplete: () => {
        void this.opts.close();
      },
      context: [createPopupDeeplink('popupPetStarterPack', { productId })],
    });
  }

  public fadeIn(): Promise<void> {
    this.timer.autoUpdate();
    return super.fadeIn();
  }

  public fadeOut() {
    this.timer.stopAutoUpdate();
    return super.fadeOut();
  }
}
